/**
 * Fuente de verdad de design tokens Vuelto (app + portal).
 * Tema claro stone + brand teal (fase 3).
 * Regenerá JSON/CSS: `npm run emit` en este package.
 *
 * @see docs/design/brand/STYLE-GUIDE.md
 */

export const brand = {
  50: '#E8F7F4',
  100: '#C5EBE4',
  500: '#0F766E',
  600: '#0D9488',
  700: '#115E59',
} as const;

/** Superficies tema claro stone — mismos roles en app y portal. */
export const surface = {
  /**
 * Canvas de página (wash cool teal-gray). Más marcado que `surface` blanco
 * para que las cards se lean como capas elevadas — no casi-blanco sobre casi-blanco.
 */
  bg: '#F4F9F7',
  bgPortal: '#F4F9F7',
  /** Capas elevadas: cards, paneles, inputs. */
  surface: '#FFFFFF',
  surfacePortal: '#FFFFFF',
  surfaceSecondary: '#EAF3F0',
  controlSurface: '#EAF3F0',
  inputBackground: '#FFFFFF',
  /**
   * Separadores / chrome de layout (Square `--border`).
   * Ink stone semitransparente — no mint sólido (#9AADA5).
   */
  border: 'rgba(28, 25, 23, 0.08)',
  /** Wash / hairlines no esenciales (más suave que border). */
  borderSoft: 'rgba(28, 25, 23, 0.06)',
  /**
   * Botones, inputs, cards accionables (Square `--border-ctl`).
   * Ink stone 14% — se lee gris-cálido sobre canvas mint, no outline teal.
   */
  borderControl: 'rgba(28, 25, 23, 0.14)',
  borderPortal: 'rgba(28, 25, 23, 0.08)',
  borderCardPortal: 'rgba(28, 25, 23, 0.14)',
  overlay: 'rgba(28, 25, 23, 0.45)',
} as const;

export const text = {
  primary: '#1C1917',
  primaryPortal: '#1C1917',
  /** Meta / ayudas de body (~7:1). */
  secondary: '#57534E',
  secondaryPortal: '#57534E',
  /** Kickers, captions, placeholders con AA (~4.6:1 sobre canvas y cards). */
  muted: '#6F6A65',
  mutedPortal: '#6F6A65',
  onBrand: '#FFFFFF',
} as const;

/**
 * Acento Crece — solo CTAs de conversión del plan pago (Activar, paywall,
 * retry, soft-limit, login con intent). No usar para acciones genéricas:
 * el teal brand sigue siendo el primary del producto.
 * Valores del proto `billing-activar-crece-vs-hoy-v1` (`--crece` / `--crece-soft`).
 */
export const crece = {
  500: '#D97745',
  soft: '#FDF0E9',
  onAccent: '#FFFFFF',
} as const;

export const semantic = {
  success: '#15803D',
  successPortal: '#15803D',
  warning: '#B45309',
  warningPortal: '#B45309',
  danger: '#B91C1C',
  dangerPortal: '#B91C1C',
  info: '#1D4ED8',
  accentSoft: 'rgba(15,118,110,0.12)',
} as const;

/** Status de dominio (pedidos / consultas). */
export const status = {
  pending: semantic.warning,
  confirmed: semantic.success,
  preparing: semantic.info,
  ready: brand[500],
  dispatched: '#7C3AED',
  delivered: text.secondary,
  picked_up: text.secondary,
  cancelled: semantic.danger,
  consultationPending: semantic.warning,
  consultationAnswered: semantic.success,
  consultationClosed: text.muted,
} as const;

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 22,
  full: 9999,
} as const;

export const tokens = {
  brand,
  crece,
  surface,
  text,
  semantic,
  status,
  spacing,
  radius,
} as const;

export type VueltoTokens = typeof tokens;
